// src/components/landing/NosotrosPage.js
import React, { useRef, useEffect } from 'react';
import './NosotrosPage.css';
import nosotrosVideo from '../../assets/abogados-video.mp4'; 
import abogado1Img from '../../assets/abogado1.jpg';       
import abogado2Img from '../../assets/abogado2.jpg';
import logoWisby from '../../assets/logos/logo_2.png';

// Componente `NosotrosPage` que renderiza esta parte de la interfaz.
const NosotrosPage = ({ t = {} }) => {
  // Referencia mutable `heroRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const heroRef = useRef(null);
  // Referencia mutable `contentRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const contentRef = useRef(null);

  useEffect(() => {
    // Función `setHeroHeight` que actualiza la información asociada.
    const setHeroHeight = () => {
      if (heroRef.current) {
        // Valor intermedio `bodyZoom` utilizado por la lógica de este bloque.
        const bodyZoom = parseFloat(getComputedStyle(document.body).zoom) || 1;
        // Valor intermedio `realHeight` utilizado por la lógica de este bloque.
        const realHeight = window.innerHeight / bodyZoom;
        heroRef.current.style.height = `${realHeight}px`;
      }
    };

    setHeroHeight();
    setTimeout(setHeroHeight, 100);
    window.addEventListener('resize', setHeroHeight);
    
    return () => window.removeEventListener('resize', setHeroHeight);
  }, []);

  // Función `scrollToContent` que encapsula una operación reutilizable de este módulo.
  const scrollToContent = () => {
    contentRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Obtener valores y stats del objeto de traducción
  const values = t.values || [];
  // Valor intermedio `stats` utilizado por la lógica de este bloque.
  const stats = t.stats || [];
  // Valor intermedio `lawyer1` utilizado por la lógica de este bloque.
  const lawyer1 = t.lawyer1 || {};
  // Valor intermedio `lawyer2` utilizado por la lógica de este bloque.
  const lawyer2 = t.lawyer2 || {};

  // Componente `LinkedInIcon` que renderiza esta parte de la interfaz.
  const LinkedInIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  );

  return (
    <div className="nosotros-page">
      {/* === HERO === */}
      <section ref={heroRef} className="nosotros-hero">
        <video className="nosotros-hero-video" autoPlay loop muted playsInline>
          <source src={nosotrosVideo} type="video/mp4" />
        </video>
        <div className="nosotros-hero-overlay"></div>
        <div className="nosotros-hero-content">
          <h1>{t.title}</h1>
          <button onClick={scrollToContent} className="nosotros-hero-button">
            {t.button}
          </button>
        </div>
      </section>

      {/* === INTRODUCCIÓN === */}
      <section ref={contentRef} className="nosotros-intro-section">
        <div className="nosotros-intro-content">
          <div className="intro-text-block">
            <h2>{t.introTitle}</h2>
            <div className="intro-divider"></div>
            <p>{t.introText}</p>
          </div>
          <div className="intro-stats">
            {stats.map((stat, index) => (
              <div key={index} className="stat-block">
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === VALORES === */}
      <section className="nosotros-values-section">
        <img src={logoWisby} alt="Wisby" className="values-bg-logo" />
        <div className="nosotros-values-content">
          <h2>{t.valuesTitle}</h2>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-item">
                <div className="value-indicator">
                  <span className="value-number">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <h3>{value.title}</h3>
                <p>{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === EQUIPO === */}
      <section className="nosotros-team-section">
        <h2>{t.teamTitle}</h2>
        <div className="team-divider"></div>

        <div className="team-grid">
          {/* Abogado 1 */}
          <div className="lawyer-card">
            <div className="lawyer-image">
              <img src={abogado1Img} alt={lawyer1.name} />
            </div>
            <div className="lawyer-info">
              <span className="lawyer-role">{lawyer1.role}</span>
              <h3>{lawyer1.name}</h3>
              <p>{lawyer1.desc}</p>
              <div className="lawyer-areas">
                {(lawyer1.areas || []).map((area, i) => (
                  <span key={i} className="area-tag">{area}</span>
                ))}
              </div>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="linkedin-btn">
                <LinkedInIcon />
                <span>{t.viewLinkedIn}</span>
              </a>
            </div>
          </div>

          {/* Abogado 2 */}
          <div className="lawyer-card">
            <div className="lawyer-image">
              <img src={abogado2Img} alt={lawyer2.name} />
            </div>
            <div className="lawyer-info">
              <span className="lawyer-role">{lawyer2.role}</span>
              <h3>{lawyer2.name}</h3>
              <p>{lawyer2.desc}</p>
              <div className="lawyer-areas">
                {(lawyer2.areas || []).map((area, i) => (
                  <span key={i} className="area-tag">{area}</span>
                ))}
              </div>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="linkedin-btn">
                <LinkedInIcon />
                <span>{t.viewLinkedIn}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* === CTA === */}
      <section className="nosotros-cta-section">
        <h2>{t.ctaTitle}</h2>
        <button className="cta-button">{t.ctaButton}</button>
      </section>
    </div>
  );
};

export default NosotrosPage;